import { useEffect, useId, useState, type FormEvent } from 'react';
import { Banner, Button, Dialog, RadioCard, Skeleton } from '../../../ds/components';
import { ApiError, errorMessage } from '../../../lib/api';
import { linkFinancingDeal, useLinkOptions } from '../api';
import type { FinancingRecord } from '../types';
import styles from './dialogs.module.css';

export interface LinkDealDialogProps {
  open: boolean;
  financing: FinancingRecord;
  onClose: () => void;
  onDone: (financing: FinancingRecord) => void;
}

/**
 * „Mit Deal verknüpfen" (Finanzierungen 07, dialog not modelled): the contact's active deals in the caller's
 * scope (`GET /financings/:id/link-options`) → `POST /financings/:id/link-deal`.
 */
export function LinkDealDialog({ open, financing, onClose, onDone }: LinkDealDialogProps) {
  const formId = useId();
  const options = useLinkOptions(financing.id, open);
  const [dealId, setDealId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [banner, setBanner] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const items = options.data?.items ?? [];

  useEffect(() => {
    if (!open) return;
    setDealId(null);
    setError(null);
    setBanner(null);
  }, [open, financing.id]);
  useEffect(() => {
    if (dealId === null && items.length === 1) setDealId(items[0]?.id ?? null);
  }, [items, dealId]);

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (pending) return;
    if (dealId === null) {
      setError('Bitte wählen Sie einen Deal.');
      return;
    }
    setPending(true);
    setBanner(null);
    try {
      onDone(await linkFinancingDeal(financing.id, dealId));
    } catch (cause) {
      if (cause instanceof ApiError && cause.fields['dealId']) setError(cause.fields['dealId']);
      else setBanner(errorMessage(cause));
    } finally {
      setPending(false);
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      width={560}
      eyebrow={financing.numberLabel}
      title="Mit Deal verknüpfen"
      subtitle={`${financing.linked.contact.name} · aktive Deals des Kontakts`}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={pending}>
            Abbrechen
          </Button>
          <Button type="submit" form={formId} loading={pending} disabled={items.length === 0}>
            Verknüpfen
          </Button>
        </>
      }
    >
      <form id={formId} className={styles.stack} onSubmit={submit} noValidate>
        {banner ? <Banner tone="danger">{banner}</Banner> : null}
        {options.isError ? <Banner tone="danger">{errorMessage(options.error)}</Banner> : null}
        {options.isPending ? <Skeleton variant="block" height={56} /> : null}
        {options.data && items.length === 0 ? (
          <p className={styles.muted}>Der Kontakt hat keinen aktiven Deal, den Sie verknüpfen können.</p>
        ) : null}
        {items.length > 0 ? (
          <fieldset className={styles.group}>
            <legend className={styles.legend}>Deal</legend>
            <div className={styles.list}>
              {items.map((item) => (
                <RadioCard
                  key={item.id}
                  name={`${formId}-deal`}
                  title={item.line}
                  description={item.stageLabel}
                  value={item.id}
                  checked={dealId === item.id}
                  onChange={() => {
                    setDealId(item.id);
                    setError(null);
                  }}
                />
              ))}
            </div>
            {error ? (
              <span className={styles.error} role="alert">
                {error}
              </span>
            ) : null}
          </fieldset>
        ) : null}
      </form>
    </Dialog>
  );
}
